import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, X } from '../ui/Icon';
import { Icon } from '../ui/Icon';

const HINT_POPOVER_W = 360;
const GAP = 8;
// Header + hint line + a 6-row textarea + the action row. Only used to decide
// which side of the button to open on; the box is then capped to what's there.
const HINT_POPOVER_H = 340;

interface Anchor {
  left: number;
  top: number;
  /** Opens upwards. False when there is no room above the button. */
  above: boolean;
  maxHeight: number;
}

interface Props {
  /** The ref, not its value: reading `.current` during render is a stale read. */
  anchorRef: React.RefObject<HTMLElement | null>;
  onSubmit: (hint: string) => void;
  onClose: () => void;
}

/**
 * The optional steer handed to gIA before it reorders a day ("Sagrada Família in
 * the morning"). Anchored to the button that opened it and portalled to the
 * body, so a panel that scrolls or clips can't cut it off.
 */
export default function AiHintPopover({ anchorRef, onSubmit, onClose }: Props) {
  const { t } = useTranslation();
  const [hint, setHint] = useState('');
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  useEffect(() => {
    function update() {
      const el = anchorRef.current;
      if (!el) { setAnchor(null); return; }
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const left = Math.max(GAP, Math.min(window.innerWidth - HINT_POPOVER_W - GAP, rect.right - HINT_POPOVER_W));
      // The button that opens this is a day header, which can sit at the very top
      // of the panel — opening upwards there puts the box off the screen. Flip it
      // below when there's no room, and cap it to the side it ended up on so the
      // Optimise button is always reachable.
      const roomAbove = rect.top - GAP - GAP;
      const roomBelow = vh - rect.bottom - GAP - GAP;
      const above = roomAbove >= HINT_POPOVER_H || roomAbove >= roomBelow;
      setAnchor({
        left,
        top: above ? rect.top - GAP : rect.bottom + GAP,
        above,
        maxHeight: Math.max(180, above ? roomAbove : roomBelow),
      });
    }
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => { window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true); };
  }, [anchorRef]);

  if (!anchor) return null;

  return createPortal(
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
      <div
        data-placement={anchor.above ? 'above' : 'below'}
        style={{
          position: 'fixed', left: anchor.left, top: anchor.top, width: HINT_POPOVER_W,
          maxHeight: anchor.maxHeight, overflowY: 'auto',
          transform: anchor.above ? 'translateY(-100%)' : undefined,
          zIndex: 50,
        }}
        className="rounded-2xl border border-violet-200 dark:border-violet-800 bg-white dark:bg-slate-900 shadow-2xl p-4"
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold uppercase tracking-widest text-violet-500">{t('map.aiHint.title')}</p>
          <button type="button" onClick={onClose}
            className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
            aria-label={t('common.close')}>
            <Icon as={X} size={14} />
          </button>
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 mb-3 leading-snug">{t('map.aiHint.subtitle')}</p>
        <textarea
          value={hint}
          onChange={e => setHint(e.target.value)}
          rows={6}
          autoFocus
          placeholder={t('map.aiHint.placeholder')}
          className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 placeholder:text-slate-400 outline-none focus:border-violet-400 dark:focus:border-violet-600 transition-colors resize-none mb-3 leading-relaxed"
        />
        <div className="flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 cursor-pointer">
            {t('common.cancel')}
          </button>
          <button type="button" onClick={() => onSubmit(hint)} className="flex-1 btn-primary gap-1.5 text-sm py-2 justify-center">
            <Icon as={Sparkles} size={14} /> {t('map.optimize')}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
